import React, { useState, useEffect } from 'react';
import { useFeatureLimits } from './FeatureLimitsProvider';
import { Badge, ProgressBar, Tooltip, OverlayTrigger, Card } from 'react-bootstrap';
import { FaExclamationTriangle, FaCheckCircle, FaLock, FaChartLine } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const FeatureUsageIndicator = ({ 
  featureName, 
  showProgress = true, 
  showBadge = true,
  size = 'sm',
  className = '',
  position = 'top-right', // 'top-right', 'top-left', 'bottom-right', 'bottom-left'
  autoCloseMs = 5000
}) => {
  const navigate = useNavigate();
  const {
    getFeatureLimit,
    hasReachedLimit,
    getRemainingLimit,
    getUsagePercentage,
    isFreeUser,
    getSubscriptionDisplayName
  } = useFeatureLimits();

  const [isVisible, setIsVisible] = useState(true);

  const limit = getFeatureLimit(featureName);
  const remaining = getRemainingLimit(featureName);
  const percentage = getUsagePercentage(featureName);
  const isBlocked = limit && limit.limit_value === 0;
  const isLimitReached = hasReachedLimit(featureName);

  const getStatusColor = () => {
    if (isBlocked) return 'danger';
    if (isLimitReached) return 'warning';
    if (percentage >= 80) return 'warning';
    return 'success';
  };

  const getStatusIcon = () => {
    if (isBlocked) return <FaLock className="me-1" />;
    if (isLimitReached) return <FaExclamationTriangle className="me-1" />;
    return <FaCheckCircle className="me-1" />;
  };

  const getStatusText = () => {
    if (isBlocked) return 'Blocked';
    if (isLimitReached) return 'Limit Reached';
    if (remaining === 0) return 'No Remaining';
    return `${remaining} remaining`;
  };

  const getTooltipText = () => {
    if (isBlocked) {
      return 'This feature is not available for free users. Upgrade to access.';
    }
    if (isLimitReached) {
      const resetDate = limit?.resetDate ? new Date(limit.resetDate).toLocaleDateString() : 'soon';
      return `Limit reached. Resets ${resetDate}.`;
    }
    return `${remaining} uses remaining out of ${limit?.limit_value || 0} total.`;
  };

  const getPositionStyles = () => {
    const baseStyles = {
      position: 'fixed',
      zIndex: 1050,
      maxWidth: '300px'
    };

    switch (position) {
      case 'top-right':
        return { ...baseStyles, top: '20px', right: '20px' };
      case 'top-left':
        return { ...baseStyles, top: '20px', left: '20px' };
      case 'bottom-right':
        return { ...baseStyles, bottom: '20px', right: '20px' };
      case 'bottom-left':
        return { ...baseStyles, bottom: '20px', left: '20px' };
      default:
        return { ...baseStyles, top: '20px', right: '20px' };
    }
  };

  // Expose visibility globally so pages can react (e.g., shift action buttons)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.featureUsageOpen = isVisible;
      window.dispatchEvent(new CustomEvent('featureUsageToggle', { detail: isVisible }));
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.featureUsageOpen = false;
        window.dispatchEvent(new CustomEvent('featureUsageToggle', { detail: false }));
      }
    };
  }, [isVisible]);

  // Auto-close after specified duration
  useEffect(() => {
    if (!isVisible || !autoCloseMs) return;
    const timerId = setTimeout(() => setIsVisible(false), autoCloseMs);
    return () => clearTimeout(timerId);
  }, [isVisible, autoCloseMs]);

  if (!limit || !isVisible) {
    return null;
  }

  const statusColor = getStatusColor();
  const statusIcon = getStatusIcon();
  const statusText = getStatusText();

  return (
    <div style={getPositionStyles()} className={`feature-usage-indicator ${className}`}>
      <Card className="shadow-sm">
        <Card.Header className="text-white py-2" style={{ backgroundColor: '#2e3192' }}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <FaChartLine className="me-2" />
              <small className="fw-bold">Feature Usage</small>
            </div>
            <button
              className="btn btn-sm btn-outline-light"
              onClick={() => setIsVisible(false)}
              style={{ padding: '2px 6px', fontSize: '12px' }}
            >
              ×
            </button>
          </div>
        </Card.Header>
        <Card.Body className="p-3">
          <div className="mb-2">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="fw-medium">{limit.feature_name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
              <Badge bg={statusColor} className="fs-7 ms-2" style={{ fontSize: '0.75rem' }}>
                {statusIcon}
                {statusText}
              </Badge>
            </div>
            
            {showProgress && !isBlocked && (
              <div className="mb-2">
                <div className="d-flex justify-content-between small text-muted mb-1">
                  <span>Usage</span>
                  <span>{limit.current_usage || 0} / {limit.limit_value}</span>
                </div>
                <ProgressBar
                  variant={statusColor}
                  now={percentage}
                  size={size}
                  label={size === 'lg' ? `${percentage}%` : undefined}
                  className={size === 'sm' ? 'progress-sm' : ''}
                />
              </div>
            )}

            {isBlocked && isFreeUser() && (
              <div className="alert alert-danger py-2 mb-2">
                <small>
                  <FaLock className="me-1" />
                  Upgrade to access this feature
                </small>
              </div>
            )}
          </div>

          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              {isFreeUser() ? (
                <span className="badge bg-secondary px-2 py-1" style={{ fontSize: '0.7rem' }}>Free Trial</span>
              ) : (
                <div className="d-flex flex-column">
                  <span className="badge px-2 py-1" style={{ fontSize: '0.7rem', backgroundColor: '#2e3192', color: '#fff', borderRadius: '12px' }}>✨ Premium</span>
                  <small className="text-muted" style={{ fontSize: '0.7rem' }}>Billed {getSubscriptionDisplayName().toLowerCase()}</small>
                </div>
              )}
            </div>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                // Navigate to limits page using React Router
                navigate('/feature-limits');
              }}
              style={{ 
                fontSize: '0.75rem', 
                padding: '2px 8px',
                borderColor: '#2e3192',
                color: '#2e3192'
              }}
            >
              View Limits
            </button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

// Compact floating indicator
export const FloatingUsageIndicator = ({ featureName, position = 'top-right' }) => {
  return (
    <FeatureUsageIndicator
      featureName={featureName}
      showProgress={true}
      showBadge={true}
      size="sm"
      position={position}
    />
  );
};

// Full dashboard indicator
export const DashboardUsageIndicator = ({ featureName }) => {
  return (
    <FeatureUsageIndicator
      featureName={featureName}
      showProgress={true}
      showBadge={true}
      size="lg"
      position="top-right"
    />
  );
};

export default FeatureUsageIndicator;